import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentResult({ success }) {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState(success ? "checking" : "cancel");
  useEffect(() => {
    if (!success) return;
    const paypal = params.get("provider") === "paypal";
    const sid = paypal ? params.get("subscription_id") : params.get("session_id");
    let tries = 0;
    let timer;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(paypal ? `/payments/paypal/status/${sid}` : `/payments/status/${sid}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (r.data.status === "expired") return setState("failed");
      } catch (e) {
        if (e?.response?.status === 404) return setState("failed");
      }
      if (cancelled) return;
      if (++tries < 15) timer = setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [success, params, refresh]);
  const view = {
    checking: [Loader2, "Confirming your payment…", "This only takes a moment.", "animate-spin text-primary"],
    paid: [CheckCircle2, "You're subscribed", "Your plan is active. All included modules are unlocked.", "text-emerald-500"],
    failed: [XCircle, "Payment not completed", "We couldn't find a completed payment for this checkout.", "text-red-500"],
    timeout: [Loader2, "Still processing", "Refresh this page in a minute to confirm your subscription.", "text-amber-500"],
    cancel: [XCircle, "Checkout cancelled", "No charge was made. You can choose a plan any time.", "text-muted-foreground"],
  }[state];
  const [Icon, title, text, cls] = view;
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="surface p-10 max-w-md w-full text-center" data-testid={`payment-${state}`}>
        <Icon className={`h-12 w-12 mx-auto ${cls}`} />
        <h1 className="text-2xl font-bold mt-4">{title}</h1>
        <p className="text-sm text-muted-foreground mt-2">{text}</p>
        <Button asChild className="mt-8" data-testid="payment-continue-btn"><Link to={state === "paid" ? "/app" : "/app/billing"}>Continue</Link></Button>
      </div>
    </div>
  );
}
